import React, { useMemo, useState } from 'react';
import {
  ShieldCheck,
  ExternalLink,
  CheckCircle2,
  ArrowRight,
  Info,
  Scale,
  Calendar,
  ChevronRight,
  Lock,
  Flag,
  Bitcoin,
  TerminalSquare,
  Search,
} from 'lucide-react';

import {
  Anchor,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Group,
  Paper,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
  useComputedColorScheme,
  Select,
} from '@mantine/core';
import { useLocation } from 'wouter';
import { useTranslation } from 'react-i18next';

import useStatus from "./statusProvider";
import useUser from "./userProvider";
import useRepos from "./repoProvider";
import CalenderStatus from "./CalendarStatus";
import ErrorScreen from "./ErrorScreen.jsx";

export default function PublicVerification({ params }) {
  const { t } = useTranslation();
  const colorScheme = useComputedColorScheme('light');
  const [selectedYear, setSelectedYear] = useState(2026);
  const [, setLocation] = useLocation();
  const {user, repo} = params;
  const {data: status, error, mutate} = useStatus(user, repo);
  const {userInfo} = useUser();
  const {data: repos} = useRepos(userInfo?.sub);

  const handleRepoSelect = (value) => {
    setLocation(`/status/${userInfo.sub}/${value}`);
  };

  const navigateInternal = (path) => (event) => {
    event.preventDefault();
    setLocation(path);
    };

  const repoInfo = useMemo(() => {
    const commits = status?.commits ?? 0;
    const stampDates = status?.timestampsdata
      ? Object.keys(status.timestampsdata).sort()
      : [];
    const firstStamp = stampDates.length > 0 ? stampDates[0] : null;
    const totalDays = firstStamp
      ? Math.round((new Date() - new Date(firstStamp + 'T00:00:00')) / 86400000)
      : 0;

    let longevityValue = '\u2014';
    let longevityUnit = 'Years';
    if (totalDays > 0) {
      if (totalDays >= 365) {
        longevityValue = (totalDays / 365.25).toFixed(1);
        longevityUnit = 'Years';
      } else if (totalDays >= 30) {
        longevityValue = (totalDays / 30.4375).toFixed(1);
        longevityUnit = 'Months';
      } else {
        longevityValue = String(totalDays);
        longevityUnit = 'Days';
      }
    }

    const lastStamp = stampDates.length > 0 ? stampDates[stampDates.length - 1] : null;

    let blockTimestamp = '\u2014';
    if (status?.lastBlockTimestamp) {
      const d = new Date(parseInt(status.lastBlockTimestamp) * 1000);
      blockTimestamp = d.toISOString().replace('T', ' ').replace(/\.\d+Z/, ' UTC');
    }

    const lastBlockHash = status?.lastBlockHash ?? '';
    const shortTxId = lastBlockHash
      ? lastBlockHash.substring(0, 8) + '\u2026' + lastBlockHash.substring(lastBlockHash.length - 6)
      : '\u2014';

    const hasBlockData = !!(status?.lastBlock && status?.lastBlockTimestamp);

    let protectedSince = '\u2014';
    if (firstStamp) {
      protectedSince = firstStamp;
    }

    return {
      owner: params?.user ?? '',
      name: params?.repo ?? '',
      status: commits > 0 ? 'Verified' : 'Unverified',
      protectedSince,
      regularity: status?.regularity ?? null,
      totalCommits: commits.toLocaleString(),
      longevityValue,
      longevityUnit,
      firstStamp,
      lastStamp,
      hasBlockData,
      latestAnchor: {
        block: status?.lastBlock ? String(status.lastBlock) : '\u2014',
        timestamp: blockTimestamp,
        txId: shortTxId,
        merkleRoot: status?.lastBlockMerkleRoot ?? '\u2014',
      },
    };
  }, [status, params]);

  const levelColor = {
     0: colorScheme === 'dark' ? '#1A1B1E' : '#ffffff',
    1: colorScheme === 'dark' ? '#0E4429' : '#9be9a8',
    2: colorScheme === 'dark' ? '#006D32' : '#40c463',
    3: colorScheme === 'dark' ? '#26A641' : '#30a14e',
    4: colorScheme === 'dark' ? '#39D353' : '#216e39',
  };

  if ((userInfo == null) && (error?.status == 403)) {
    const url = new URL("/api/login", document.location);
    const params = new URLSearchParams({cont: document.location});
    url.search = params.toString();
    document.location = url.toString();
    return null;
    }

  if (error) {
    return (
      <ErrorScreen
        status={error.status}
        title={t('Repository status unavailable')}
        message={t('We could not load repository status and timeline data at the moment.')}
        onRetry={() => mutate()}
      />
    );
  }

  const years = [2026, 2025, 2024];

  return (
    <Box mih="100vh" py={40}>
      <Container size="lg">
        <Stack gap="xl">
          <Group justify="space-between" align="center">
            <Anchor href="/" onClick={navigateInternal('/')} c="inherit" underline="never">
              <Group gap="sm">
                <ThemeIcon color="teal" variant="light" radius="xl">
                  <ShieldCheck size={18} />
                </ThemeIcon>
                <Text fw={800} fz="lg">
                  Timestamp GIT
                </Text>
              </Group>
            </Anchor>

            {(() => { const r = Array.isArray(repos) ? repos : repos?.repos; return r && r.length > 0; })() && (
              <Select
                placeholder={t('Search repositories...')}
                data={(Array.isArray(repos) ? repos : repos?.repos || []).map(r => ({ value: r.name, label: `${userInfo.sub}/${r.name}` }))}
                onChange={handleRepoSelect}
                searchable
                clearable
                leftSection={<Search size={14} />}
                nothingFoundMessage={t('No repositories found')}
                limit={50}
                style={{ width: 320 }}
              />
            )}

            <Group gap="lg">
              <Anchor href="/concept" onClick={navigateInternal('/concept')} c="dimmed" fw={600} fz="sm" underline="never">
                {t('HOW IT WORKS')}
              </Anchor>
              <Anchor href={repoInfo.lastStamp ? `/verification/${user}/${repo}/${repoInfo.lastStamp}` : '#'} onClick={repoInfo.lastStamp ? navigateInternal(`/verification/${user}/${repo}/${repoInfo.lastStamp}`) : undefined} fw={700} fz="sm" underline="never">
                {t('VERIFY MATHEMATICS')}
              </Anchor>
            </Group>
          </Group>

          <Paper
            radius="xl"
            p="xl"
            withBorder
            bg={colorScheme === 'dark' ? 'dark.8' : 'gray.0'}
          >
            <Stack gap="lg">
              <Badge
                variant="light"
                color="teal"
                size="lg"
                radius="xl"
                leftSection={<Lock size={14} />}
              >
                {t('Immutable Ledger Active')}
              </Badge>

              <Title order={1} fz="h1" fw={800}>
                <Anchor
                  href={`https://github.com/${repoInfo.owner}/${repoInfo.name}`}
                  target="_blank"
                  underline="never"
                  c="inherit"
                >
                  <Group gap="xs" wrap="nowrap">
                    <Text component="span" c="dimmed" fw={400}>
                      {repoInfo.owner} /
                    </Text>
                    <Text component="span" fw={800}>
                      {repoInfo.name}
                    </Text>
                    <ExternalLink size={18} style={{ opacity: 0.5 }} />
                  </Group>
                </Anchor>
              </Title>

              <SimpleGrid cols={{ base: 1, md: 2, lg: 4 }} spacing="md">
                <Paper p="md" radius="md" withBorder bg="transparent">
                  <Text fz="xs" tt="uppercase" fw={800} c="dimmed">
                    {t('Proof Longevity')}
                  </Text>
                  <Text fz="xl" fw={700}>
                    {repoInfo.longevityValue} {t(repoInfo.longevityUnit)}
                  </Text>
                </Paper>

                <Paper p="md" radius="md" withBorder bg="transparent">
                  <Text fz="xs" tt="uppercase" fw={800} c="dimmed">
                    {t('Earliest Anchor')}
                  </Text>
                  <Text fz="xl" fw={700}>
                    {repoInfo.protectedSince}
                  </Text>
                </Paper>

                <Paper p="md" radius="md" withBorder bg="transparent">
                  <Text fz="xs" tt="uppercase" fw={800} c="dimmed">
                    {t('Daily Regularity')}
                  </Text>
                  <Text fz="xl" fw={700} c="teal">
                    {repoInfo.regularity != null ? `${repoInfo.regularity}%` : '\u2014'} {t('Coverage')}
                  </Text>
                </Paper>

                <Paper p="md" radius="md" withBorder bg="transparent">
                  <Text fz="xs" tt="uppercase" fw={800} c="dimmed">
                    {t('Security Protocol')}
                  </Text>
                  <Text fz="xl" fw={700}>
                    Bitcoin / OTS
                  </Text>
                </Paper>
              </SimpleGrid>
            </Stack>
          </Paper>

          <Stack gap="md">
            <Title order={2} fz="lg" fw={700} tt="uppercase">
              {t('Chain of Trust Landmarks')}
            </Title>

            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
              <Paper
                radius="md" p="lg" withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => repoInfo.lastStamp && setLocation(`/verification/${user}/${repo}/${repoInfo.lastStamp}`)}
              >
                <Group justify="space-between" align="center">
                  <Group gap="sm" align="center">
                    <ThemeIcon variant="light" color="teal" radius="xl">
                      <CheckCircle2 size={16} />
                    </ThemeIcon>
                    <Stack gap={2}>
                      <Text fw={700} fz="sm">
                        {t('Last Successful Anchor')}
                      </Text>
                      <Text fz="xs" fw={600} tt="uppercase" c="dimmed">
                        {repoInfo.hasBlockData
                          ? repoInfo.latestAnchor.timestamp.split(' ')[0]
                          : (repoInfo.lastStamp ?? '\u2014')}
                      </Text>
                      <Text fz="sm" c="dimmed">
                        {repoInfo.hasBlockData
                          ? t('Block #{{block}} confirmed', { block: repoInfo.latestAnchor.block })
                          : t('Timestamp recorded')}
                      </Text>
                    </Stack>
                  </Group>
                  <ArrowRight size={16} />
                </Group>
              </Paper>

              <Paper
                radius="md" p="lg" withBorder
                style={{ cursor: 'pointer' }}
                onClick={() => repoInfo.firstStamp && setLocation(`/verification/${user}/${repo}/${repoInfo.firstStamp}`)}
              >
                <Group justify="space-between" align="center">
                  <Group gap="sm" align="center">
                    <ThemeIcon variant="light" color="blue" radius="xl">
                      <Flag size={16} />
                    </ThemeIcon>
                    <Stack gap={2}>
                      <Text fw={700} fz="sm">
                        {t('Initial Proof (Genesis)')}
                      </Text>
                      <Text fz="xs" fw={600} tt="uppercase" c="dimmed">
                        {repoInfo.protectedSince}
                      </Text>
                      <Text fz="sm" c="dimmed">
                        {t('First prior-art anchor recorded')}
                      </Text>
                    </Stack>
                  </Group>
                  <ArrowRight size={16} />
                </Group>
              </Paper>
            </SimpleGrid>
          </Stack>

           <CalenderStatus user={user} repo={repo} status={status?.timestampsdata} />

          <Stack gap="md">
            <Title order={2} fz="lg" fw={700} tt="uppercase">
              {t('Latest Anchor Verification Data')}
            </Title>

            <Paper radius="lg" p="lg" withBorder>
              <Stack gap="sm">
                <Group justify="space-between" align="flex-start">
                  <Text fw={600} c="dimmed">
                    {t('Bitcoin Block Reference')}
                  </Text>
                  <Anchor
                    href={`https://mempool.space/block/${repoInfo.latestAnchor.block}`}
                    target="_blank"
                    underline="never"
                    fw={700}
                  >
                    <Group gap={4}>
                      <Text inherit>#{repoInfo.latestAnchor.block}</Text>
                      <ExternalLink size={14} />
                    </Group>
                  </Anchor>
                </Group>
                <Divider />

                <Group justify="space-between" align="flex-start">
                  <Text fw={600} c="dimmed">
                    {t('Cryptographic Merkle Root')}
                  </Text>
                  <Text fz="sm" maw="60%" style={{ wordBreak: 'break-all' }}>
                    {repoInfo.latestAnchor.merkleRoot}
                  </Text>
                </Group>
                <Divider />

                <Group justify="space-between" align="flex-start">
                  <Text fw={600} c="dimmed">
                    {t('Transaction Hash')}
                  </Text>
                  <Text fz="sm">{repoInfo.latestAnchor.txId}</Text>
                </Group>
              </Stack>
            </Paper>
          </Stack>

          <Paper radius="lg" p="lg" withBorder bg="teal.0" style={{borderColor: 'var(--mantine-color-teal-4)'}}>
            <Stack align="center" gap="sm">
              <Group gap="sm">
                <ThemeIcon variant="filled" color="teal" radius="xl" size="lg">
                  <ShieldCheck size={20} />
                </ThemeIcon>
                <Stack gap={2}>
                  <Text fw={700} fz="lg">{t('Show your users this project is protected')}</Text>
                  <Text c="dimmed" size="sm" maw={500} ta="center">
                    {t('Add a cryptographically verifiable badge to your README.')}
                  </Text>
                </Stack>
              </Group>
              <Button size="lg" variant="filled" color="teal" radius="md" leftSection={<ShieldCheck size={20} />} onClick={() => setLocation(`/connected/${user}/${repo}`)}>
                {t('Get Status Badge')}
              </Button>
            </Stack>
          </Paper>

          <Stack align="center" gap="md">
            <Text fz="xs" c="dimmed" tt="uppercase" fw={600}>{t('Downloads & Exports')}</Text>
            <Group justify="center" gap="md">
              <Button size="md" variant="default" leftSection={<TerminalSquare size={16} />} component="a" href={`/api/audit/${user}/${repo}`}>
                {t('DOWNLOAD FULL AUDIT LEDGER (CSV)')}
              </Button>
              <Button size="md" variant="default" leftSection={<Bitcoin size={16} />} component="a" href={`/api/report/${user}/${repo}/${repoInfo.lastStamp}`}>
                {t('DOWNLOAD PDF CERTIFICATE')}
              </Button>
            </Group>
          </Stack>

          <Stack align="center" gap={4} pb="md">
            <Scale size={14} />
            <Text fz="xs" c="dimmed" ta="center" lh={1.6}>
              {t("This public ledger provides mathematically undeniable evidence of the repository's existence over time.")}
              <br />
              {t('Verification is powered by the OpenTimestamps protocol anchored into the Bitcoin Blockchain.')}
              <br />
              {t('(c) 2026 RSJ Software GmbH - Germering, Germany')}
            </Text>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}